import React, { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { recipientsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';

export default function Recipients() {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    bank_name: '',
    account_number: '',
    swift_code: ''
  });

  useEffect(() => {
    loadRecipients();
  }, []);

  const loadRecipients = async () => {
    try {
      const response = await recipientsAPI.getAll();
      setRecipients(response.data);
    } catch (error) {
      toast.error('Failed to load recipients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRecipient) {
        await recipientsAPI.update(editingRecipient.id, formData);
        toast.success('Recipient updated successfully');
      } else {
        await recipientsAPI.create(formData);
        toast.success('Recipient added successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadRecipients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (recipient) => {
    setEditingRecipient(recipient);
    setFormData({
      name: recipient.name,
      email: recipient.email || '',
      phone: recipient.phone,
      country: recipient.country,
      bank_name: recipient.bank_name,
      account_number: recipient.account_number,
      swift_code: recipient.swift_code || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recipient?')) return;
    try {
      await recipientsAPI.delete(id);
      toast.success('Recipient deleted successfully');
      loadRecipients();
    } catch (error) {
      toast.error('Failed to delete recipient');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      country: '',
      bank_name: '',
      account_number: '',
      swift_code: ''
    });
    setEditingRecipient(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">Recipients</h1>
            <p className="text-muted-foreground">Manage your saved recipients</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-recipient-dialog-btn" variant="accent" className="gap-2">
                <Plus className="w-4 h-4" /> Add Recipient
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="recipient-dialog">
              <DialogHeader>
                <DialogTitle>{editingRecipient ? 'Edit Recipient' : 'Add New Recipient'}</DialogTitle>
                <DialogDescription>
                  {editingRecipient ? 'Update recipient information' : 'Enter recipient details for money transfers'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    data-testid="recipient-name-input"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email (Optional)</Label>
                  <Input
                    data-testid="recipient-email-input"
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    data-testid="recipient-phone-input"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    data-testid="recipient-country-input"
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input
                    data-testid="recipient-bank-input"
                    id="bank_name"
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="account_number">Account Number</Label>
                  <Input
                    data-testid="recipient-account-input"
                    id="account_number"
                    value={formData.account_number}
                    onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="swift_code">SWIFT/BIC Code (Optional)</Label>
                  <Input
                    data-testid="recipient-swift-input"
                    id="swift_code"
                    value={formData.swift_code}
                    onChange={(e) => setFormData({ ...formData, swift_code: e.target.value })}
                  />
                </div>
                <Button data-testid="recipient-submit-btn" type="submit" className="w-full bg-accent hover:bg-accent/90">
                  {editingRecipient ? 'Update Recipient' : 'Add Recipient'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto" />
          </div>
        ) : recipients.length === 0 ? (
          <Card data-testid="no-recipients-card" className="p-12 text-center">
            <p className="text-muted-foreground mb-4">No recipients added yet</p>
            <p className="text-sm text-muted-foreground">Add your first recipient to start sending money</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recipients.map((recipient) => (
              <Card key={recipient.id} data-testid={`recipient-card-${recipient.id}`} className="p-6">
                <div className="mb-4">
                  <h3 className="text-xl font-semibold mb-1">{recipient.name}</h3>
                  <p className="text-sm text-muted-foreground">{recipient.country}</p>
                </div>
                <div className="space-y-2 text-sm mb-4">
                  {recipient.email && <p className="text-muted-foreground">Email: {recipient.email}</p>}
                  <p className="text-muted-foreground">Phone: {recipient.phone}</p>
                  <p className="text-muted-foreground">Bank: {recipient.bank_name}</p>
                  <p className="text-muted-foreground font-mono">Account: {recipient.account_number}</p>
                  {recipient.swift_code && <p className="text-muted-foreground">SWIFT: {recipient.swift_code}</p>}
                </div>
                <div className="flex gap-2">
                  <Button data-testid={`edit-recipient-${recipient.id}`} onClick={() => handleEdit(recipient)} variant="outline" size="sm" className="flex-1 gap-2">
                    <Edit className="w-4 h-4" /> Edit
                  </Button>
                  <Button data-testid={`delete-recipient-${recipient.id}`} onClick={() => handleDelete(recipient.id)} variant="destructive" size="sm" className="flex-1 gap-2">
                    <Trash2 className="w-4 h-4" /> Delete
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}